import { useMemo, useLayoutEffect, useRef } from "react";
import { useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useStore } from "@/store/useStore";

export default function ProductModel({ url }) {
  const { scene } = useGLTF(url);
  const outer = useRef();

  const explode = useStore((s) => s.explode);
  const shading = useStore((s) => s.shading);
  const opacity = useStore((s) => s.opacity);

  // Clone scene + materials so instance edits are isolated.
  const { model, parts } = useMemo(() => {
    const clone = scene.clone(true);
    const parts = [];
    clone.traverse((o) => {
      if (o.isMesh) {
        o.material = o.material.clone();
        o.castShadow = true;
        o.receiveShadow = true;
      }
    });
    // Normalise to ~3 units and center.
    const box = new THREE.Box3().setFromObject(clone);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const scale = 3.1 / maxDim;
    clone.position.set(-center.x, -center.y, -center.z);

    const wrap = new THREE.Group();
    wrap.add(clone);
    wrap.scale.setScalar(scale);

    // Collect meshes w/ explode direction relative to model center (local).
    clone.traverse((o) => {
      if (o.isMesh) {
        const wp = new THREE.Vector3();
        o.getWorldPosition(wp);
        const dir = wp.clone().sub(center.clone().multiplyScalar(scale)).normalize();
        if (!isFinite(dir.x) || dir.length() < 0.01) dir.set(0, 1, 0);
        parts.push({ mesh: o, orig: o.position.clone(), dir });
      }
    });
    return { model: wrap, parts };
  }, [scene]);

  // Apply shading + transparency.
  useLayoutEffect(() => {
    parts.forEach(({ mesh }) => {
      const m = mesh.material;
      m.wireframe = shading === "wireframe";
      const xray = shading === "xray";
      const op = xray ? Math.min(opacity, 0.32) : opacity;
      m.transparent = op < 0.995 || xray;
      m.opacity = op;
      m.depthWrite = !m.transparent;
      m.needsUpdate = true;
    });
  }, [shading, opacity, parts]);

  // Smoothly animate explode.
  const target = useRef(0);
  const current = useRef(0);
  target.current = explode;
  useFrame(() => {
    current.current += (target.current - current.current) * 0.12;
    const e = current.current;
    parts.forEach(({ mesh, orig, dir }) => {
      mesh.position.set(
        orig.x + dir.x * e * 2.4,
        orig.y + dir.y * e * 2.4,
        orig.z + dir.z * e * 2.4
      );
    });
  });

  return <primitive ref={outer} object={model} />;
}
